/**
 * Temporary component for the "Skip" button of the first step
 * The whole process should be using components
 */

// Import dependencies
import { html, LitElement, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef } from 'lit/directives/ref.js';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-setup-wizard-step-1-skip' )
export class AutomatorSetupWizardStep1Skip extends LitElement {
	// Define properties
	@property( { type: String, attribute: 'url-next-step' } ) accessor urlNextStep;

	@property( { type: String, attribute: 'url-connect-account' } ) accessor urlConnectAccount;

	// Create a reference to the dialog that doesn't exist yet
	$dialog = createRef();

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="button">
				<uo-button
					color="transparent"
					@click=${ this.createDialog }
				>
					${ escapeHTML( __( 'Skip this', 'uncanny-automator' ) ) }
				</uo-button>
			</div>
		`;
	}

	/**
	 * Creates the dialog with the log data
	 * It also resizes the dialog when the child component shows or hides the sidebar
	 */
	createDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uo-dialog
					heading=${ escapeHTML( __( 'Are you sure?', 'uncanny-automator' ) ) }
					size="small"
					open
				>
					${ this._templateDialogContent }
					${ this._templateDialogConfirmButton }

					<uo-button
						slot="secondary-action"
						color="secondary"
						href=${ this.urlNextStep }
					>
						${ escapeHTML( __( 'Skip for now', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * Generates the template for the dialog confirm button based on the account type.
	 * If Automator Pro is active, this method returns `undefined`, effectively omitting the confirm button from the dialog.
	 * Otherwise, it returns an HTML template for a button that prompts the user to sign up.
	 *
	 * @return {TemplateResult|undefined} An HTML template for the confirm button if Automator Pro is not active, otherwise `undefined`.
	 */
	get _templateDialogConfirmButton() {
		// If Automator Pro is active, we don't need to show the button
		if ( UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return;
		}

		return html`
			<uo-button
				slot="primary-action"
				href=${ this.urlConnectAccount }
			>
				${ escapeHTML( __( 'Sign up now', 'uncanny-automator' ) ) }
			</uo-button>
		`;
	}

	/**
	 * Retrieves the appropriate dialog content based on the account type.
	 *
	 * @return {String} The dialog content string for either Pro or Free account features.
	 */
	get _templateDialogContent() {
		if ( UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return escapeHTML( __( 'Your pro license gives you access to unlimited app credits, pro updates, premium support and much more.', 'uncanny-automator' ) );
		}

		return escapeHTML( __( 'Your free account gives you access to Slack, Google Sheets, Facebook, exclusive discounts, updates and much more.', 'uncanny-automator' ) );
	}
}
